import type { OrderStats, ChannelStat } from '../../types/order'
import { formatEuros } from '../../lib/format'
import { salesCents, productSalesCents, deliveryFeesCents, avgOrderCents, type SalesSlice } from './salesBasis'

// ─── Sales by channel table ─────────────────────────────────────────────────────
// One column per channel the order was placed on (Counter, Kiosk, Webshop, then
// each platform, then Total), read top-to-bottom:
//
//   Sales, of which delivery fees / verpakkingskosten
//   Product sales (sales − delivery fees) → commission → COGS → product margin
//   Delivery fees → the commission charged on them → made on delivery
//
// Both fees are sales; only verpakkingskosten are product. Delivery stands in
// its own block and is NEVER summed with the product margin: the platform
// commission comes off the margin whole, and the part charged on the delivery
// fee is shown a second time against the fee. All figures come straight from
// orderStats.byChannel, ex VAT for financial users (salesBasis.ts holds the
// incl-VAT variant for everyone else).
//
// This is ONE dimension only — where the order was placed. How it reached the
// customer (dine-in / takeaway / delivery) is the FulfilmentBreakdown next to it;
// the two are never crossed in one table.

export const CHANNEL_DOT: Record<string, string> = {
  POS: '#CA4330',
  KIOSK: '#E26F61',
  WEBSHOP: '#853F82',
  UBER_EATS: '#0e9f6e',
  THUISBEZORGD: '#DC9C4E',
  HUBRISE_OTHER: '#9CA3AF',
}

export const channelColor = (channel: string) => CHANNEL_DOT[channel] ?? '#9CA3AF'

// The API names the till channel "POS"; operators call it the counter.
export const channelLabel = (c: Pick<ChannelStat, 'channel' | 'displayName'>) =>
  c.channel === 'POS' ? 'Counter' : c.displayName

export const pctOf = (part: number, base: number) => (base > 0 ? `${Math.round((part / base) * 100)}%` : '—')

/** Product margin with its % of PRODUCT sales, coloured by sign — shared by both sales tables. */
export const ProductMarginCell = ({ cents, productSales }: { cents: number; productSales: number }) => {
  const up = cents >= 0
  return (
    <span className={up ? 'text-emerald-700' : 'text-red-600'}>
      {formatEuros(cents)}
      <span className={`ml-1.5 text-xs font-medium ${up ? 'text-emerald-600/70' : 'text-red-500/80'}`}>{pctOf(cents, productSales)}</span>
    </span>
  )
}

export interface SourceCol {
  key: string
  label: string
  color: string
  kind: 'channel' | 'total'
  orders: number
  // The table's money basis: ex VAT after discount for financial users,
  // incl. VAT before discount otherwise (see salesBasis.ts).
  sales: number          // goods + verpakkingskosten + delivery fees
  deliveryFees: number   // of which
  packaging: number      // of which
  productSales: number   // sales − delivery fees
  avgOrder: number       // product sales ÷ orders
  // Ex-VAT chain — financial users only (0 otherwise, rows not rendered)
  commission: number         // the WHOLE platform commission; 0 on our own channels
  salesAfterComm: number     // product sales − commission
  cogs: number
  productMargin: number      // after commission − COGS
  deliveryCommission: number // the part of `commission` charged on the delivery fee
  deliveryResult: number     // delivery fees − that part; never added to productMargin
  /**
   * Statiegeld collected. NOT part of any figure above — it is a deposit
   * passing through us to the supplier, so it is neither sales nor margin.
   * Reported on its own row so it can be seen without polluting revenue.
   */
  deposit: number
  /**
   * Promo discounts, incl. VAT (the API serves them gross, with no ex-VAT twin).
   * Per channel since API 2026-10-08 (`discountCents`); the Total column reads
   * `totalDiscountCents`, and Σ channels == total by contract.
   */
  discounts: number
}

type MoneySlice = SalesSlice &
  Pick<
    ChannelStat,
    | 'packagingFeeExVatCents'
    | 'platformCommissionCents'
    | 'netRevenueCents'
    | 'cogsCents'
    | 'contributionMarginCents'
    | 'deliveryFeeCommissionCents'
    | 'deliveryResultCents'
    | 'depositCents'
  >

// ChannelStat and the OrderStats totals share these fields, so one mapper
// fills a channel column and the Total column alike.
const moneyOf = (s: MoneySlice, canFin: boolean) => ({
  orders: s.orderCount,
  sales: salesCents(s, canFin),
  deliveryFees: deliveryFeesCents(s, canFin),
  packaging: canFin ? (s.packagingFeeExVatCents ?? 0) : s.packagingFeeCents,
  productSales: productSalesCents(s, canFin),
  avgOrder: avgOrderCents(s, canFin),
  commission: s.platformCommissionCents ?? 0,
  salesAfterComm: s.netRevenueCents ?? 0,
  cogs: s.cogsCents ?? 0,
  productMargin: s.contributionMarginCents ?? 0,
  deliveryCommission: s.deliveryFeeCommissionCents ?? 0,
  deliveryResult: s.deliveryResultCents ?? 0,
  deposit: s.depositCents ?? 0,
})

// One column per byChannel row, in the API's sortOrder (own channels first,
// platforms after). A channel with no orders in the period is absent from
// byChannel and therefore from the table — Kiosk only shows on days it sold.
export const buildSourceColumns = (stats: OrderStats, canFin: boolean): { columns: SourceCol[]; total: SourceCol } => {
  const columns = [...(stats.byChannel ?? [])]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((c): SourceCol => ({
      key: c.channel,
      label: channelLabel(c),
      color: channelColor(c.channel),
      kind: 'channel',
      ...moneyOf(c, canFin),
      discounts: c.discountCents,
    }))

  const total: SourceCol = {
    key: 'TOTAL', label: 'Total', color: '#111827', kind: 'total',
    ...moneyOf(stats, canFin),
    discounts: stats.totalDiscountCents ?? 0,
  }

  return { columns, total }
}

interface MetricRow {
  label: string
  cell: (c: SourceCol) => React.ReactNode
  tone: string
  highlight?: boolean
  indent?: boolean
}

export const SourceBreakdown = ({ columns, total, canFin }: { columns: SourceCol[]; total: SourceCol; canFin: boolean }) => {
  const allCols = [...columns, total]
  const totalClass = (c: SourceCol) => (c.kind === 'total' ? 'border-l border-gray-200 bg-gray-50/60' : '')
  const basis = canFin ? '(ex. VAT)' : '(incl. VAT)'
  const amountOrDash = (cents: number) => (cents > 0 ? formatEuros(cents) : '—')
  // A period without delivery fees has no delivery rows at all: product sales
  // then equals sales, and the table reads as a plain sales → margin funnel.
  const hasDelivery = total.deliveryFees > 0

  const salesRows: MetricRow[] = [
    { label: `Sales ${basis}`, tone: 'text-gray-900 font-semibold', cell: (c) => formatEuros(c.sales) },
    // The two fees INSIDE sales. Each row disappears when nothing was charged.
    ...(hasDelivery ? [{ label: 'of which delivery fees', tone: 'text-gray-500', indent: true, cell: (c: SourceCol) => amountOrDash(c.deliveryFees) }] : []),
    ...(total.packaging > 0 ? [{ label: 'of which verpakkingskosten', tone: 'text-gray-500', indent: true, cell: (c: SourceCol) => amountOrDash(c.packaging) }] : []),
    { label: 'Share of sales', tone: 'text-gray-500', cell: (c) => pctOf(c.sales, total.sales) },
  ]

  // The commission rows read from the data: our own channels carry 0, so
  // they show "—" without a client-side list of which channels are ours.
  const marginRows: MetricRow[] = canFin
    ? [
        ...(hasDelivery ? [{ label: 'Product sales (ex. VAT)', tone: 'text-gray-900 font-semibold', cell: (c: SourceCol) => formatEuros(c.productSales) }] : []),
        { label: '− Platform commission', tone: 'text-red-500', cell: (c) => (c.commission > 0 ? `−${formatEuros(c.commission)}` : '—') },
        { label: '= After commission', tone: 'text-gray-700', cell: (c) => (c.commission > 0 ? formatEuros(c.salesAfterComm) : '—') },
        { label: '− COGS', tone: 'text-gray-500', cell: (c) => `−${formatEuros(c.cogs)}` },
        { label: '= Product margin', tone: 'font-bold', highlight: true, cell: (c) => <ProductMarginCell cents={c.productMargin} productSales={c.productSales} /> },
      ]
    : []

  // Delivery on its own: what the fee made after the platform's cut on it.
  const deliveryRows: MetricRow[] = canFin && hasDelivery
    ? [
        { label: 'Delivery fees (ex. VAT)', tone: 'text-gray-900 font-semibold', cell: (c) => amountOrDash(c.deliveryFees) },
        { label: '− Commission on the fee', tone: 'text-red-500', cell: (c) => (c.deliveryCommission > 0 ? `−${formatEuros(c.deliveryCommission)}` : '—') },
        { label: '= Made on delivery', tone: 'font-bold text-gray-900', cell: (c) => (c.deliveryFees > 0 ? formatEuros(c.deliveryResult) : '—') },
      ]
    : []

  const opsRows: MetricRow[] = [
    { label: 'Avg. order size', tone: 'text-gray-700', cell: (c) => formatEuros(c.avgOrder) },
    // Gross (incl. VAT) in every column, whatever the table's basis: the API has no
    // ex-VAT discount figure. A channel without promotions shows "—".
    { label: 'Discounts applied (incl. VAT)', tone: 'text-gray-700', cell: (c) => amountOrDash(c.discounts) },
    // Statiegeld is a deposit passing through us (no VAT, never sales): the one
    // collected amount that stays outside the blocks above, shown gross so it
    // reconciles against the payments report. Hidden when none was charged.
    ...(total.deposit > 0 ? [{ label: 'Statiegeld (not sales)', tone: 'text-gray-500', cell: (c: SourceCol) => amountOrDash(c.deposit) }] : []),
    { label: 'Orders', tone: 'text-gray-900 font-semibold', cell: (c) => c.orders },
  ]

  const renderRow = (r: MetricRow) => (
    <tr key={r.label} className={`border-b border-gray-50 ${r.highlight ? 'bg-emerald-50/40' : ''}`}>
      <td className={`py-2.5 pr-4 text-sm text-gray-500 whitespace-nowrap ${r.indent ? 'pl-4' : ''}`}>{r.label}</td>
      {allCols.map((c) => (
        <td key={c.key} className={`py-2.5 px-3 text-right tabular-nums ${r.tone} ${totalClass(c)}`}>{r.cell(c)}</td>
      ))}
    </tr>
  )

  const spacer = (key: string) => <tr key={key}><td className="py-1.5" colSpan={allCols.length + 1} /></tr>

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="py-3 pr-4" />
            {allCols.map((c) => (
              <th key={c.key} className={`py-3 px-3 ${totalClass(c)}`}>
                <div className="flex items-center justify-end gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                  <span className={`font-semibold whitespace-nowrap ${c.kind === 'total' ? 'text-gray-900' : 'text-gray-700'}`}>{c.label}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {salesRows.map(renderRow)}
          {marginRows.map(renderRow)}
          {deliveryRows.length > 0 && spacer('delivery-spacer')}
          {deliveryRows.map(renderRow)}
          {spacer('ops-spacer')}
          {opsRows.map(renderRow)}
        </tbody>
      </table>
      {/* On screen, not in a tooltip: the POS runs on touchscreens. */}
      <p className="mt-3 text-xs text-gray-400">
        Avg. order size = sales without the delivery fee ÷ orders.
        {deliveryRows.length > 0 && ' The commission on the fee is part of the platform commission above, so “made on delivery” is not added to the product margin.'}
      </p>
    </div>
  )
}
